import React, { useState, useRef, useEffect } from 'react';
import { sessionsApi } from '../../api/sessions';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import { manualSummaryBlockedMessage } from '../../utils/summaryGate';
import './CreateSummaryButton.css';

// A 409 is either the writer's edited head (review a merged draft) or a
// summary already being created (show that message; the writer can try again).
function summaryConflictIsBusy(error) {
  const detail = error?.detail;
  const detailText = typeof detail === 'string'
    ? detail
    : (detail && typeof detail.message === 'string' ? detail.message : '');
  const text = `${error?.message || ''} ${detailText}`.toLowerCase();
  return text.includes('already being created');
}

/**
 * Button to manually create an interim summary during a session.
 * Includes an expandable input for optional instructions.
 *
 * @param {string} sessionId - Current session ID
 * @param {function} onSummaryCreated - Callback with interim summary data
 * @param {function} onError - Callback for errors
 * @param {function} onCreateStart - Callback when creation request starts
 *   (drives the "Creating a summary…" status chip — a label swap on this
 *   small button is not enough feedback for a 30s operation)
 * @param {function} onWriterEditedHead - Callback when the head summary is the
 *   writer's edited version: instead of appending an AI summary, route into
 *   the hold-and-offer merge-draft review (same path as the auto refresh).
 */
export function CreateSummaryButton({
  sessionId,
  onSummaryCreated,
  onError,
  onCreateStart,
  onWriterEditedHead,
  disabled = false,
}) {
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [instructions, setInstructions] = useState('');
  const inputRef = useRef(null);

  // Focus input when expanded
  useEffect(() => {
    if (expanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [expanded]);

  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();

  const handleCreateSummary = async () => {
    if (!sessionId || loading || disabled) return;

    setLoading(true);
    try {
      // Hold-and-offer guard: if the head is the writer's edited version,
      // do not append an AI summary — route into the merge-draft review
      // instead (same path the auto refresh uses).
      let st = null;
      try {
        st = await sessionsApi.getSummaryStatus(sessionId);
      } catch (_) { /* status optional; fall through to create */ }
      if (st?.head_source === 'writer_edited' && onWriterEditedHead) {
        setInstructions('');
        setExpanded(false);
        onWriterEditedHead();
        return;
      }

      // Never ask the writer to pay for a summary the backend will refuse:
      // the status carries the create path's own verdict.
      const blocked = manualSummaryBlockedMessage(st);
      if (blocked) {
        setExpanded(false);
        if (onError) onError(blocked);
        return;
      }

      const ok = await askPaid('create_summary');
      if (!ok) return;
      if (onCreateStart) {
        onCreateStart();
      }
      const options = instructions.trim() ? { instructions: instructions.trim() } : {};
      const summary = await sessionsApi.createInterimSummary(sessionId, options);
      if (onSummaryCreated) {
        onSummaryCreated(summary);
      }
      // Reset state after successful creation
      setInstructions('');
      setExpanded(false);
    } catch (error) {
      // A writer-edited 409 (race after the status check) opens the
      // merge-draft review. A busy 409 keeps the server's message.
      const busy = error.status === 409 && summaryConflictIsBusy(error);
      if (error.status === 409 && onWriterEditedHead && !busy) {
        setInstructions('');
        setExpanded(false);
        onWriterEditedHead();
        return;
      }
      console.error('Failed to create summary:', error);
      if (onError) {
        const message = error.message || error.response?.data?.detail || 'Failed to create summary';
        onError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreateSummary();
    } else if (e.key === 'Escape') {
      setExpanded(false);
      setInstructions('');
    }
  };

  const toggleExpanded = async () => {
    if (disabled) return;
    if (expanded) {
      // Collapsing - just create without instructions
      handleCreateSummary();
      return;
    }
    // Pre-flight before opening the instructions box, so the writer is told
    // up front instead of after typing instructions and seeing a cost.
    if (sessionId) {
      setLoading(true);
      let st = null;
      try {
        st = await sessionsApi.getSummaryStatus(sessionId);
      } catch (_) { /* unknown status: let the create path decide */ }
      setLoading(false);
      const blocked = manualSummaryBlockedMessage(st);
      if (blocked) {
        if (onError) onError(blocked);
        return;
      }
    }
    setExpanded(true);
  };

  if (expanded) {
    return (
      <div className="create-summary-expanded">
        {paidDialog}
        <div className="create-summary-expanded__header">
          <span className="create-summary-expanded__title">Create Summary</span>
          <button
            type="button"
            className="create-summary-expanded__close"
            onClick={() => { setExpanded(false); setInstructions(''); }}
            title="Cancel"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <input
          ref={inputRef}
          type="text"
          className="create-summary-expanded__input"
          placeholder="Optional: Add instructions (e.g., 'Focus on character decisions')"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
        <div className="create-summary-expanded__actions">
          <button
            type="button"
            className="create-summary-expanded__btn create-summary-expanded__btn--secondary"
            onClick={() => { setExpanded(false); setInstructions(''); }}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="create-summary-expanded__btn create-summary-expanded__btn--primary"
            onClick={handleCreateSummary}
            disabled={loading || !sessionId || disabled}
          >
            {loading ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="create-summary-btn"
      onClick={toggleExpanded}
      disabled={loading || !sessionId || disabled}
      title="Create summary of current conversation state"
      aria-label="Create Summary"
    >
      {loading ? (
        <span className="create-summary-btn__loading">...</span>
      ) : (
        <>
          <span className="create-summary-btn__icon" aria-hidden="true">📝</span>
          <span className="create-summary-btn__text">Create Summary</span>
        </>
      )}
    </button>
  );
}

// Backwards compatibility alias
export const CheckpointButton = CreateSummaryButton;

export default CreateSummaryButton;
